/**
 * 지출결의등록(type1)
 * 연결(과목1:증빙N) → 임시저장 묶음 누적 → 저장(F10) 시 결의번호 1개 채번
 * 전자증빙·소득명세는 연결 시 임시점유, 저장 시 결의로 승격 / 삭제 시 해제
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, FormProvider, useWatch } from 'react-hook-form';
import styled from 'styled-components';
import { useAuth } from 'contexts/AuthContext';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import { PageContainer, CommonCss, ActionButton } from 'styles/common.styles';
import { theme, SEARCH_LABEL_W } from 'styles/theme';
import { hoverExpandScrollbarCss } from 'styles/scrollbar.styles';
import useScrollbarHoverProps from 'hooks/useScrollbarHoverProps';
import {
  FormArea,
  CollapsibleSearchPanel,
  ActionButtonGroup,
  getCurrentFiscalYear,
} from 'components/forms';
import { HiGridComponent, useHiGridHook, hiCol } from 'components/grid/hiGrid';
import {
  GridExportProvider,
  GridExportFocusScope,
  useRegisterGridExport,
  useExcelExportAction,
} from 'components/grid/excel';
import {
  ActiveGridProvider,
  ActiveGridScope,
  ActiveGridHeader,
  useActiveGridCrudActions,
} from 'contexts/ActiveGridContext';
import {
  GridPanel,
  GridBody,
  Meta,
} from 'components/budget/BudgetFormationChrome.styles';
import { showAlert, showConfirm } from 'utils/toastBridge';
import CodeLookupDialog from 'components/budget/CodeLookupDialog';
import IncomeStatementDialog from 'components/expense/IncomeStatementDialog';
import ExpenseResolutionType1DetailDialog, {
  buildDetailBundlesFromDraft,
  buildDetailBundlesFromResolution,
} from 'components/expense/ExpenseResolutionType1DetailDialog';
import ExpenseType1ChatPanel from 'components/ai/ExpenseType1ChatPanel';
import { loadDepts, filterDepts, getCachedDepts } from 'master/deptStore';
import {
  EVIDENCE_SOURCE,
  ensureEvidenceSeed,
  filterOpenEvidences,
  loadEvidences,
  fetchEvidences,
  detailFromEvidence,
  evidenceSourceLabel,
  occupyEvidencesDraft,
  releaseEvidencesDraft,
  promoteEvidencesDraftToResolution,
  releaseEvidencesByResolution,
  reconcileOrphanEvidenceLinks,
} from 'expense/expenseEvidenceStore';
import {
  detailFromIncomeStatement,
  ensureIncomeEarnerSeed,
  fetchIncomeEarners,
  fetchIncomeStatements,
  occupyStatementsDraft,
  releaseStatementsDraft,
  promoteStatementsDraftToResolution,
  releaseStatementsByResolution,
} from 'expense/expenseIncomeStore';
import {
  TYPE1_DRAFT_NO_LABEL,
  loadType1Draft,
  fetchType1Draft,
  saveType1Draft,
  clearType1Draft,
  emptyDraftSession,
  linkBundleToDraft,
  draftSessionToListRow,
  buildResolutionFromDraft,
  isType1DraftRow,
  hasIssuedResolutionNo,
  createDraftSessionUid,
  summarizeDraftSession,
} from 'expense/resolutionType1DraftStore';
import {
  EXPENSE_STATUS,
  expenseStatusLabel,
  createResolutionDetailUid,
  emptyResolutionDetailRow,
  loadResolutionBundle,
  saveResolutionBundle,
  loadObligationBundle,
  saveObligationBundle,
  ensureObligationForResolution,
  filterResolutionsForRegister,
  requestResolutionApproval,
} from 'expense/expenseStore';
import {
  listProjectsAggregated,
  listSubjectsForSelectedProject,
} from 'expense/resolutionType1Utils';

const PAYEE_GRID_ID = 'expense-res-t1-payee';
const PROJECT_GRID_ID = 'expense-res-t1-project';
const SUBJECT_GRID_ID = 'expense-res-t1-subject';
const RESULT_GRID_ID = 'expense-res-t1-result';

const WorkRow = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  gap: 8px;
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  ${hoverExpandScrollbarCss}

  /* 섹션 간 높이 분배로 영역이 0으로 줄어들거나 겹치지 않도록 각자 고유 높이 유지 */
  > * {
    flex: 0 0 auto;
  }
`;

const SectionPanel = styled(GridPanel)`
  flex: 0 0 auto;
  min-height: ${(p) => p.$minHeight || 'auto'};
  height: ${(p) => p.$height || 'auto'};
  overflow: hidden;
`;

const MidRow = styled.div`
  display: flex;
  gap: 8px;
  height: 260px;
  flex: 0 0 auto;
  min-height: 260px;

  > * {
    flex: 1 1 0;
    min-width: 0;
    min-height: 0;
    height: 100%;
  }
`;

const MidPanel = styled(GridPanel)`
  flex: 1 1 auto;
  height: 100%;
  min-height: 0;
  overflow: hidden;
`;

const Hint = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.secondary};
  line-height: 1.45;
  padding: 0 2px 2px;
`;

const PAYEE_BODY_H = 180;
const RESULT_BODY_H = 200;

function payeeRowKey(r) {
  return String(r?.detailUid || r?.evidenceUid || r?.statementUid || r?.id || '').trim();
}

function ExpenseResolutionType1Inner() {
  const { user } = useAuth();
  const tenantCode = useTenantCode();
  const prefsUserId = buildPrefsUserId(user, tenantCode);
  const draftUserKey = prefsUserId;
  const scrollbarHoverProps = useScrollbarHoverProps();

  const methods = useForm({
    defaultValues: {
      fiscalYear: getCurrentFiscalYear(),
      includeFund: 'N',
      searchDeptCode: '',
      searchDeptName: '',
      searchDeptDisp: '',
    },
  });
  const { getValues, setValue, control } = methods;
  const searchDeptNameWatch = useWatch({ control, name: 'searchDeptName' });

  const [selectedProject, setSelectedProject] = useState(null);
  /** localStorage 임시연결 세션 — 사업 1개 · 묶음 N개 */
  const [draftSession, setDraftSession] = useState(null);
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const [incomeOpen, setIncomeOpen] = useState(false);
  const [deptLookupOpen, setDeptLookupOpen] = useState(false);
  const [deptRows, setDeptRows] = useState(() => getCachedDepts(tenantCode) || []);
  const [listCount, setListCount] = useState(0);
  const [payeeSum, setPayeeSum] = useState(0);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailMaster, setDetailMaster] = useState(null);
  const [detailBundles, setDetailBundles] = useState([]);

  const payeeRef = useRef(null);
  const projectRef = useRef(null);
  const subjectRef = useRef(null);
  const resultRef = useRef(null);
  const selectedProjectRef = useRef(null);

  const payeeGrid = useHiGridHook({
    gridRef: payeeRef,
    enableContextMenu: true,
    defaultRow: emptyResolutionDetailRow({
      detailUid: '',
      mod: 'A',
      vatYn: 'N',
    }),
  });
  const projectGrid = useHiGridHook({
    gridRef: projectRef,
    enableContextMenu: false,
  });
  const subjectGrid = useHiGridHook({
    gridRef: subjectRef,
    enableContextMenu: false,
  });
  const resultGrid = useHiGridHook({
    gridRef: resultRef,
    enableContextMenu: false,
  });

  const dims = useCallback(
    () => ({
      tenantCode,
      fiscalYear: String(getValues('fiscalYear') || getCurrentFiscalYear()).trim(),
    }),
    [tenantCode, getValues]
  );

  const enrichResultRow = useCallback((m) => {
    const lines = Array.isArray(m?.subjectLines) ? m.subjectLines : [];
    return {
      ...m,
      id: m.resolutionUid,
      statusNm: expenseStatusLabel(m.status),
      subjectCount: lines.length || (m.subjectCode ? 1 : 0),
      subjectSummary:
        lines.length > 1
          ? `${lines[0]?.subjectCode || ''} 외 ${lines.length - 1}`
          : m.subjectCode || lines[0]?.subjectCode || '',
    };
  }, []);

  /** 결의내역 = 임시행(있으면 상단) + 실결의 */
  const buildResultList = useCallback(
    (masters, session) => {
      const real = (masters || []).map(enrichResultRow);
      const draftRow = draftSessionToListRow(session);
      return draftRow ? [draftRow, ...real] : real;
    },
    [enrichResultRow]
  );

  const refreshProjects = useCallback(() => {
    const d = dims();
    const includeFund = String(getValues('includeFund') || 'N') === 'Y';
    const deptCode = String(getValues('searchDeptCode') || '').trim();
    const rows = listProjectsAggregated(tenantCode, d.fiscalYear, { includeFund, deptCode });
    projectRef.current?.setData?.(rows);
    return rows;
  }, [dims, getValues, tenantCode]);

  const refreshSubjects = useCallback(
    (projectRow) => {
      const d = dims();
      const code = String(projectRow?.projectCode || '').trim();
      if (!code) {
        subjectRef.current?.setData?.([]);
        return [];
      }
      const subjects = listSubjectsForSelectedProject(
        tenantCode,
        d.fiscalYear,
        projectRow || {}
      ).map((s) => ({
        ...s,
        applyAmt: Number(s.applyAmt) || 0,
      }));
      subjectRef.current?.setData?.(subjects);
      return subjects;
    },
    [dims, tenantCode]
  );

  /** 사업 행 선택 → 과목내역 갱신 */
  const handleSelectProject = useCallback(
    (row) => {
      if (!row?.projectCode) return;
      selectedProjectRef.current = row;
      setSelectedProject(row);
      refreshSubjects(row);
    },
    [refreshSubjects]
  );

  const handleSearch = useCallback(async () => {
    const d = dims();
    if (!d.fiscalYear) {
      showAlert('회계연도를 입력하세요.');
      return;
    }
    ensureEvidenceSeed(tenantCode, d.fiscalYear);
    ensureIncomeEarnerSeed({ tenantCode });
    await fetchEvidences(tenantCode, d.fiscalYear);
    await fetchType1Draft(d, draftUserKey);
    await fetchIncomeEarners({ tenantCode });
    await fetchIncomeStatements(d);

    const bundle = await loadResolutionBundle(d);
    const aliveUids = (bundle.masters || [])
      .filter((m) => m.mod !== 'D')
      .map((m) => m.resolutionUid)
      .filter(Boolean);

    // 살아있는 임시세션 UID도 고아 정리에서 보존
    const draft = loadType1Draft(d, draftUserKey);
    setDraftSession(draft);
    const aliveDraftUids = draft?.draftSessionUid ? [draft.draftSessionUid] : [];
    reconcileOrphanEvidenceLinks(tenantCode, d.fiscalYear, aliveUids, aliveDraftUids);

    const projects = refreshProjects();
    const deptCode = String(getValues('searchDeptCode') || '').trim();
    const deptSet = new Set(
      deptCode
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    );

    // 선택 사업이 부서 조건에 맞으면 과목 유지·갱신, 아니면 초기화
    let proj = selectedProjectRef.current;
    if (proj?.projectCode) {
      if (deptSet.size && !deptSet.has(String(proj.deptCode || '').trim())) {
        proj = null;
        selectedProjectRef.current = null;
        setSelectedProject(null);
        subjectRef.current?.setData?.([]);
      } else {
        const key = `${proj.deptCode}|${proj.projectCode}|${proj.fundCode || ''}`;
        const hit =
          projects.find((p) => p.id === key) ||
          projects.find(
            (p) =>
              p.projectCode === proj.projectCode &&
              (!deptSet.size || p.deptCode === proj.deptCode)
          );
        if (hit) {
          handleSelectProject(hit);
        } else {
          refreshSubjects(proj);
        }
      }
    } else if (projects.length === 1) {
      handleSelectProject(projects[0]);
    } else {
      subjectRef.current?.setData?.([]);
    }

    const filter = {
      deptCode: deptCode || undefined,
      deptName: String(getValues('searchDeptName') || '').trim() || undefined,
    };
    const masters = filterResolutionsForRegister(bundle.masters, filter);
    const list = buildResultList(masters, draft);
    resultRef.current?.setData?.(list);
    setListCount(list.length);
  }, [
    dims,
    tenantCode,
    draftUserKey,
    refreshProjects,
    refreshSubjects,
    buildResultList,
    getValues,
    handleSelectProject,
  ]);

  const refreshPayeeSum = useCallback(() => {
    const sum = (payeeRef.current?.getData?.() || [])
      .filter((r) => r.mod !== 'D')
      .reduce((s, r) => s + (Number(r.amount) || 0), 0);
    setPayeeSum(sum);
  }, []);

  const getChatContext = useCallback(
    () => ({
      tenantCode,
      fiscalYear: String(getValues('fiscalYear') || getCurrentFiscalYear()).trim(),
      draftUserKey,
      includeFund: String(getValues('includeFund') || 'N') === 'Y',
      searchDeptCode: String(getValues('searchDeptCode') || '').trim(),
      selectedProject: selectedProjectRef.current,
      payees: (payeeRef.current?.getData?.() || []).filter((r) => r.mod !== 'D'),
      subjects: (subjectRef.current?.getData?.() || []).filter((r) => r.mod !== 'D'),
    }),
    [tenantCode, draftUserKey, getValues]
  );

  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const all = await loadDepts(tenantCode);
        if (live) setDeptRows(Array.isArray(all) ? all : []);
      } catch {
        if (live) setDeptRows(getCachedDepts(tenantCode) || []);
      }
    })();
    return () => {
      live = false;
    };
  }, [tenantCode, deptLookupOpen]);

  useEffect(() => {
    payeeGrid.onAfterSaveCell = (rowData, { field } = {}) => {
      if (field === 'amount') refreshPayeeSum();
    };
    return () => {
      payeeGrid.onAfterSaveCell = undefined;
    };
  }, [payeeGrid, refreshPayeeSum]);

  useEffect(() => {
    projectGrid.onGridCellClicked = (row) => handleSelectProject(row);
    projectGrid.onSelectRow = (row) => handleSelectProject(row);
    projectGrid.onRowClicked = ({ data }) => handleSelectProject(data);
    return () => {
      projectGrid.onGridCellClicked = undefined;
      projectGrid.onSelectRow = undefined;
      projectGrid.onRowClicked = undefined;
    };
  }, [projectGrid, handleSelectProject]);

  /** 결의/임시묶음 상세 — 과목별 증빙·채주 */
  const openResolutionDetail = useCallback(
    async (row) => {
      if (!row) {
        showAlert('상세 조회할 결의를 선택하세요.');
        return;
      }
      const d = dims();
      if (isType1DraftRow(row)) {
        const session =
          draftSession ||
          loadType1Draft(d, draftUserKey) ||
          (row.draftSessionUid
            ? { draftSessionUid: row.draftSessionUid, bundles: [], project: row }
            : null);
        const full = loadType1Draft(d, draftUserKey) || session;
        setDetailMaster(row);
        setDetailBundles(buildDetailBundlesFromDraft(full));
        setDetailOpen(true);
        return;
      }
      const bundle = await loadResolutionBundle(d);
      const master =
        (bundle.masters || []).find((m) => m.resolutionUid === row.resolutionUid) || row;
      // 저장 시 스냅샷이 있으면 우선 사용
      if (Array.isArray(master.bundles) && master.bundles.length) {
        setDetailMaster({ ...row, ...master });
        setDetailBundles(
          master.bundles.map((b) => ({
            bundleUid: b.bundleUid,
            subjectCode: b.subjectCode,
            subjectName: b.subjectName,
            applyAmt: b.applyAmt,
            payees: (b.payees || []).filter((p) => p.mod !== 'D'),
          }))
        );
      } else {
        setDetailMaster({ ...row, ...master });
        setDetailBundles(buildDetailBundlesFromResolution(master, bundle.details || []));
      }
      setDetailOpen(true);
    },
    [dims, draftSession, draftUserKey]
  );

  const handleOpenDetailClick = useCallback(() => {
    const checked = resultRef.current?.getCheckedData?.() || [];
    const row = checked[0] || resultRef.current?.getSelectedRow?.();
    openResolutionDetail(row);
  }, [openResolutionDetail]);

  useEffect(() => {
    resultGrid.onGridCellDoubleClicked = (row) => openResolutionDetail(row);
    return () => {
      resultGrid.onGridCellDoubleClicked = undefined;
    };
  }, [resultGrid, openResolutionDetail]);

  /** 소득명세 다이얼로그용 — 세션 없으면 빈 임시세션 생성(채번 전 UID만) */
  const ensureDraftSession = useCallback(() => {
    const d = dims();
    if (draftSession?.draftSessionUid) return draftSession;
    const existing = loadType1Draft(d, draftUserKey);
    if (existing?.draftSessionUid) {
      setDraftSession(existing);
      return existing;
    }
    const created = emptyDraftSession({
      draftSessionUid: createDraftSessionUid(),
      fiscalYear: d.fiscalYear,
      tenantCode,
      project: null,
      bundles: [],
    });
    saveType1Draft(d, draftUserKey, created);
    setDraftSession(created);
    return created;
  }, [dims, draftSession, draftUserKey, tenantCode]);

  const openIncomeDialog = useCallback(() => {
    ensureDraftSession();
    setIncomeOpen(true);
  }, [ensureDraftSession]);

  const appendPayees = useCallback(
    (rows) => {
      const list = (Array.isArray(rows) ? rows : [rows]).filter(Boolean);
      if (!list.length) return;
      const existing = payeeRef.current?.getData?.() || [];
      const existEv = new Set(existing.map((r) => r.evidenceUid).filter(Boolean));
      const next = [
        ...existing,
        ...list
          .filter((r) => !r.evidenceUid || !existEv.has(r.evidenceUid))
          .map((r) => ({
          ...r,
          detailUid: r.detailUid || createResolutionDetailUid(),
          id: r.detailUid || r.evidenceUid || r.statementUid || createResolutionDetailUid(),
          mod: 'A',
        })),
      ];
      payeeRef.current?.setData?.(next);
      refreshPayeeSum();
    },
    [refreshPayeeSum]
  );

  const handleChatDone = useCallback(
    async (data) => {
      const fx = data?.uiEffects || {};
      if (fx.selectProject?.projectCode) {
        handleSelectProject(fx.selectProject);
      }
      if (Array.isArray(fx.appendPayees) && fx.appendPayees.length) {
        appendPayees(fx.appendPayees);
      }
      if (Array.isArray(fx.clearPayeeKeys) && fx.clearPayeeKeys.length) {
        const keys = new Set(fx.clearPayeeKeys.map(String));
        const remaining = (payeeRef.current?.getData?.() || []).filter((r) => {
          const key = payeeRowKey(r);
          return !key || !keys.has(key);
        });
        payeeRef.current?.setData?.(remaining);
        refreshPayeeSum();
      }
      handleSearch();
    },
    [handleSelectProject, appendPayees, refreshPayeeSum, handleSearch]
  );

  const evidenceLookupConfig = useMemo(() => {
    const d = dims();
    ensureEvidenceSeed(tenantCode, d.fiscalYear);
    const allOpen = filterOpenEvidences(loadEvidences(tenantCode, d.fiscalYear));
    const toRow = (r) => ({
      ...r,
      id: r.evidenceUid,
      sourceNm: evidenceSourceLabel(r.source),
    });
    return {
      title: '전자증빙 (미결) — 채주 생성',
      defaultTabId: 'card',
      tabs: [
        {
          id: 'card',
          label: '법인카드',
          rows: allOpen.filter((r) => r.source === EVIDENCE_SOURCE.CARD).map(toRow),
          columns: [
            hiCol.roTextColumn({ header: '사용일', field: 'useDate', size: 100, align: 'center' }),
            hiCol.roTextColumn({ header: '카드', field: 'cardNo', size: 130 }),
            hiCol.roTextColumn({ header: '가맹점', field: 'merchantName', size: 140 }),
            hiCol.roNumberColumn({ header: '금액', field: 'amount', size: 100, align: 'right' }),
            hiCol.roTextColumn({ header: '승인번호', field: 'approveNo', size: 110 }),
          ],
        },
        {
          id: 'tax',
          label: '세금계산서',
          rows: allOpen.filter((r) => r.source === EVIDENCE_SOURCE.TAX).map(toRow),
          columns: [
            hiCol.roTextColumn({ header: '사용일', field: 'useDate', size: 100, align: 'center' }),
            hiCol.roTextColumn({ header: '거래처', field: 'merchantName', size: 140 }),
            hiCol.roTextColumn({ header: '사업자번호', field: 'businessNo', size: 120 }),
            hiCol.roNumberColumn({ header: '금액', field: 'amount', size: 100, align: 'right' }),
            hiCol.roNumberColumn({ header: '부가세', field: 'vatAmt', size: 90, align: 'right' }),
          ],
        },
      ],
      enableSearch: true,
      searchConfig: {
        mode: 'unified',
        keywordLabel: '검색',
        keywordFields: ['merchantName', 'cardNo', 'approveNo', 'businessNo', 'remark'],
      },
    };
  }, [dims, tenantCode, evidenceOpen]);

  /** 전자증빙 → 채주 그리드만 추가 (점유는 연결 시) */
  const applyEvidences = useCallback(
    (selected) => {
      const list = Array.isArray(selected) ? selected : selected ? [selected] : [];
      if (!list.length) {
        showAlert('전자증빙을 선택하세요.');
        return false;
      }
      const existing = payeeRef.current?.getData?.() || [];
      const existEv = new Set(existing.map((r) => r.evidenceUid).filter(Boolean));
      const fresh = list.filter((e) => e.evidenceUid && !existEv.has(e.evidenceUid));
      if (!fresh.length) {
        showAlert('이미 채주에 반영된 증빙입니다.', 'warning');
        return false;
      }
      appendPayees(fresh.map((e) => detailFromEvidence(e, '')));
      setEvidenceOpen(false);
      showAlert(`전자증빙 ${fresh.length}건을 채주에 추가했습니다.`, 'success');
      return true;
    },
    [appendPayees]
  );

  const handleIncomeSaved = useCallback(
    (rows) => {
      const existing = payeeRef.current?.getData?.() || [];
      const withoutSt = existing.filter((r) => !r.statementUid);
      const payees = (rows || []).map((st) => detailFromIncomeStatement(st, ''));
      payeeRef.current?.setData?.([
        ...withoutSt,
        ...payees.map((r) => ({
          ...r,
          detailUid: r.detailUid || createResolutionDetailUid(),
          id: r.statementUid || createResolutionDetailUid(),
        })),
      ]);
      refreshPayeeSum();
      showAlert(`소득명세 ${payees.length}건을 채주에 반영했습니다.`, 'success');
    },
    [refreshPayeeSum]
  );

  /** 연결용 과목 — 체크 1개만. 없으면 선택행 1개 */
  const collectLinkSubject = useCallback(() => {
    const checked = (subjectRef.current?.getCheckedData?.() || []).filter(
      (r) => r.mod !== 'D' && r.subjectCode
    );
    if (checked.length > 1) {
      return { error: '과목은 1개만 체크하세요. (연결 = 과목1 : 증빙N)' };
    }
    if (checked.length === 1) return { subject: checked[0] };
    const sel = subjectRef.current?.getSelectedRow?.();
    if (sel?.subjectCode) return { subject: sel };
    return { error: '과목을 1개 선택(체크)하세요.' };
  }, []);

  /**
   * 연결 — 과목1 : 채주N 묶음을 임시세션에 추가 (결의번호 미채번)
   * 동일 세션은 사업 1개만 허용 · 증빙/소득명세는 draft 점유
   */
  const handleLinkBundle = useCallback(async () => {
    const d = dims();
    const project = selectedProjectRef.current;
    if (!project?.projectCode) {
      showAlert('사업내역에서 사업을 선택하세요.');
      return;
    }

    const subjectPick = collectLinkSubject();
    if (subjectPick.error) {
      showAlert(subjectPick.error);
      return;
    }
    const subject = subjectPick.subject;

    const payees = (payeeRef.current?.getCheckedData?.() || []).filter((r) => r.mod !== 'D');
    if (!payees.length) {
      showAlert('연결할 채주(증빙)를 체크하세요.');
      return;
    }

    const current = draftSession || loadType1Draft(d, draftUserKey);
    const linked = linkBundleToDraft(current, {
      project: {
        ...project,
        fiscalYear: d.fiscalYear,
        tenantCode,
      },
      subject,
      payees,
    });
    if (!linked.ok) {
      showAlert(linked.error || '연결에 실패했습니다.');
      return;
    }

    const nextSession = {
      ...linked.session,
      fiscalYear: d.fiscalYear,
      tenantCode,
    };
    const sum = summarizeDraftSession(nextSession);
    const ok = await showConfirm(
      `과목 ${subject.subjectCode} · 채주 ${payees.length}건을 연결할까요?\n` +
        `(임시저장 · 결의번호 미채번)\n` +
        `누적 묶음 ${sum.bundles.length}개 · 합계 ${sum.resolutionAmt.toLocaleString()}원`
    );
    if (!ok) return;

    saveType1Draft(d, draftUserKey, nextSession);
    setDraftSession(nextSession);

    // 연결 시점부터 임시점유 (미결 목록에서 제외)
    const evUids = payees.map((p) => p.evidenceUid).filter(Boolean);
    const stUids = payees.map((p) => p.statementUid).filter(Boolean);
    occupyEvidencesDraft(tenantCode, d.fiscalYear, evUids, nextSession.draftSessionUid);
    occupyStatementsDraft(d, stUids, nextSession.draftSessionUid);

    // 연결된 채주는 그리드에서 제거
    const linkedKeys = new Set(payees.map(payeeRowKey).filter(Boolean));
    const remaining = (payeeRef.current?.getData?.() || []).filter((r) => {
      const key = payeeRowKey(r);
      return !key || !linkedKeys.has(key);
    });
    payeeRef.current?.setData?.(remaining);
    refreshPayeeSum();
    // 연결 후 과목·채주 체크 해제 (다음 묶음 준비)
    subjectRef.current?.clearRowChecks?.();
    payeeRef.current?.clearRowChecks?.();

    handleSearch();
    showAlert(
      `연결 완료 — 임시저장 ${TYPE1_DRAFT_NO_LABEL} · 묶음 ${sum.bundles.length}개`,
      'success'
    );
  }, [
    dims,
    collectLinkSubject,
    draftSession,
    draftUserKey,
    tenantCode,
    refreshPayeeSum,
    handleSearch,
  ]);

  /** 저장(F10) — 임시묶음 → 결의번호 1개 채번 · 점유 승격 */
  const handleSaveResolution = useCallback(async () => {
    const d = dims();
    const session = loadType1Draft(d, draftUserKey) || draftSession;
    const sum = summarizeDraftSession(session);
    if (!sum.bundles.length) {
      showAlert('연결된 임시묶음이 없습니다. 먼저 「연결」하세요.');
      return;
    }

    const lines = sum.subjectLines
      .map((s) => `· ${s.subjectCode} ${s.subjectName}: ${(s.applyAmt || 0).toLocaleString()}원`)
      .join('\n');
    const ok = await showConfirm(
      `임시연결 ${sum.bundles.length}묶음을 하나의 지출결의로 저장할까요?\n` +
        `사업 ${session.project?.projectCode || ''} · 결의금액 ${sum.resolutionAmt.toLocaleString()}원\n\n${lines}`
    );
    if (!ok) return;

    const built = buildResolutionFromDraft(session, d.fiscalYear);
    if (!built?.master) {
      showAlert('임시묶음을 결의로 변환할 수 없습니다.');
      return;
    }

    let resBundle = await loadResolutionBundle(d);
    let oblBundle = await loadObligationBundle(d);
    const ensured = ensureObligationForResolution(d, built.master, oblBundle, resBundle);
    const master = {
      ...ensured.resolutionMaster,
      // 상세 조회용 — 연결 묶음 스냅샷 보관
      bundles: sum.bundles.map((b) => ({
        bundleUid: b.bundleUid,
        subjectCode: b.subjectCode,
        subjectName: b.subjectName,
        balanceUid: b.balanceUid,
        fundCode: b.fundCode,
        fundName: b.fundName,
        applyAmt: b.applyAmt,
        payees: (b.payees || []).map((p) => ({
          ...p,
          resolutionUid: ensured.resolutionMaster.resolutionUid,
        })),
      })),
    };
    oblBundle = ensured.obligationBundle || oblBundle;

    const uid = master.resolutionUid;
    let masters = [...(resBundle.masters || [])];
    const idx = masters.findIndex((m) => m.resolutionUid === uid);
    if (idx >= 0) masters[idx] = { ...masters[idx], ...master, mod: 'U' };
    else masters.push({ ...master, mod: 'A' });

    let details = (resBundle.details || []).filter((x) => x.resolutionUid !== uid);
    details = [...details, ...built.details];

    const saved = await saveResolutionBundle(d, masters, details);
    if (oblBundle) {
      await saveObligationBundle(d, oblBundle.masters, oblBundle.vendors, oblBundle.contracts);
    }

    const savedMaster = saved.masters.find((m) => m.resolutionUid === uid);
    const resNo = savedMaster?.resolutionNo || master.resolutionNo || '';
    const evUids = built.details.map((x) => x.evidenceUid).filter(Boolean);
    const stUids = built.details.map((x) => x.statementUid).filter(Boolean);

    promoteEvidencesDraftToResolution(tenantCode, d.fiscalYear, {
      draftSessionUid: session.draftSessionUid,
      resolutionUid: uid,
      resolutionNo: resNo,
      evidenceUids: evUids,
    });
    promoteStatementsDraftToResolution(d, {
      draftSessionUid: session.draftSessionUid,
      resolutionUid: uid,
      resolutionNo: resNo,
      statementUids: stUids,
    });

    clearType1Draft(d, draftUserKey);
    setDraftSession(null);
    handleSearch();
    showAlert(`지출결의 ${resNo || uid} 을(를) 등록했습니다.`, 'success');
  }, [dims, draftUserKey, draftSession, tenantCode, handleSearch]);

  const getSelectedResultRows = useCallback(() => {
    const checked = resultRef.current?.getCheckedData?.() || [];
    if (checked.length) return checked;
    const sel = resultRef.current?.getSelectedRow?.();
    return sel ? [sel] : [];
  }, []);

  /** 채번된 결의만 — 승인요청/GW용 */
  const getSelectedIssuedResolutionUids = useCallback(() => {
    const rows = getSelectedResultRows();
    if (!rows.length) return { uids: [], reason: 'empty' };
    const issued = rows.filter(hasIssuedResolutionNo);
    if (!issued.length) {
      return { uids: [], reason: 'draft-only' };
    }
    return {
      uids: [...new Set(issued.map((r) => r.resolutionUid).filter(Boolean))],
      reason: 'ok',
    };
  }, [getSelectedResultRows]);

  const handleDeleteResolutions = useCallback(async () => {
    const rows = getSelectedResultRows();
    if (!rows.length) {
      showAlert('삭제할 결의를 선택(체크)하세요.');
      return;
    }

    const d = dims();
    const draftRows = rows.filter(isType1DraftRow);
    const realRows = rows.filter((r) => !isType1DraftRow(r));

    // 임시저장 행 — 세션 삭제 + draft 점유 해제
    if (draftRows.length) {
      const session = draftSession || loadType1Draft(d, draftUserKey);
      const okDraft = await showConfirm(
        `임시저장(${TYPE1_DRAFT_NO_LABEL})을 삭제할까요?\n연결된 전자증빙·소득명세는 미결로 복귀합니다.`
      );
      if (!okDraft) return;
      if (session?.draftSessionUid) {
        releaseEvidencesDraft(tenantCode, d.fiscalYear, session.draftSessionUid);
        releaseStatementsDraft(d, session.draftSessionUid);
        clearType1Draft(d, draftUserKey);
        setDraftSession(null);
      }
    }

    if (!realRows.length) {
      handleSearch();
      if (draftRows.length) {
        showAlert('임시저장을 삭제하고 증빙·소득명세 연결을 해제했습니다.', 'success');
      }
      return;
    }

    const uids = [...new Set(realRows.map((r) => r.resolutionUid).filter(Boolean))];
    const bundle = await loadResolutionBundle(d);
    const targets = (bundle.masters || []).filter((m) => uids.includes(m.resolutionUid));
    const blocked = targets.filter(
      (m) => m.status !== EXPENSE_STATUS.DRAFT && m.status !== EXPENSE_STATUS.REJECTED
    );
    if (blocked.length) {
      showAlert('작성중·반려 건만 삭제할 수 있습니다.');
      if (draftRows.length) handleSearch();
      return;
    }
    const ok = await showConfirm(
      `결의 ${uids.length}건을 삭제할까요?\n연결된 전자증빙·소득명세는 미결로 복귀합니다.`
    );
    if (!ok) {
      if (draftRows.length) handleSearch();
      return;
    }

    const uidSet = new Set(uids);
    const masters = (bundle.masters || []).map((m) =>
      uidSet.has(m.resolutionUid) ? { ...m, mod: 'D' } : m
    );
    const details = (bundle.details || []).map((x) =>
      uidSet.has(x.resolutionUid) ? { ...x, mod: 'D' } : x
    );
    await saveResolutionBundle(d, masters, details);
    uids.forEach((uid) => {
      releaseEvidencesByResolution(tenantCode, d.fiscalYear, uid);
      releaseStatementsByResolution(d, uid);
    });
    handleSearch();
    showAlert('결의를 삭제하고 증빙·소득명세 연결을 해제했습니다.', 'success');
  }, [
    getSelectedResultRows,
    dims,
    draftSession,
    draftUserKey,
    tenantCode,
    handleSearch,
  ]);

  const handleRequestApproval = useCallback(async () => {
    const { uids, reason } = getSelectedIssuedResolutionUids();
    if (reason === 'empty') {
      showAlert('승인요청할 결의를 선택(체크)하세요.');
      return;
    }
    if (reason === 'draft-only') {
      showAlert(
        `임시저장(${TYPE1_DRAFT_NO_LABEL})은 승인요청할 수 없습니다. 저장(F10)으로 결의번호를 채번한 뒤 진행하세요.`
      );
      return;
    }
    const ok = await showConfirm(`선택 ${uids.length}건을 승인요청할까요?`);
    if (!ok) return;
    const d = dims();
    let bundle = await loadResolutionBundle(d);
    for (const uid of uids) {
      const m = (bundle.masters || []).find((x) => x.resolutionUid === uid);
      if (!m || m.mod === 'D') continue;
      if (m.status !== EXPENSE_STATUS.DRAFT && m.status !== EXPENSE_STATUS.REJECTED) {
        showAlert(`${m.resolutionNo || uid}: 작성중·반려만 승인요청할 수 있습니다.`);
        return;
      }
      const result = requestResolutionApproval(bundle.masters, bundle.details, uid);
      bundle = { masters: result.masters, details: result.details };
    }
    await saveResolutionBundle(d, bundle.masters, bundle.details);
    handleSearch();
    showAlert(`승인요청 ${uids.length}건 처리했습니다.`, 'success');
  }, [getSelectedIssuedResolutionUids, dims, handleSearch]);

  const handleGwApproval = useCallback(async () => {
    const { uids, reason } = getSelectedIssuedResolutionUids();
    if (reason === 'empty') {
      showAlert('GW 연동결재할 결의를 선택(체크)하세요.');
      return;
    }
    if (reason === 'draft-only') {
      showAlert(
        `임시저장(${TYPE1_DRAFT_NO_LABEL})은 GW 연동결재할 수 없습니다. 저장(F10)으로 결의번호를 채번한 뒤 진행하세요.`
      );
      return;
    }
    const ok = await showConfirm(
      `선택 ${uids.length}건을 GW 연동결재로 올릴까요?\n(PoC: 그룹웨어 실연동 없이 승인요청 상태로 전환)`
    );
    if (!ok) return;
    const d = dims();
    let bundle = await loadResolutionBundle(d);
    for (const uid of uids) {
      const m = (bundle.masters || []).find((x) => x.resolutionUid === uid);
      if (!m || m.mod === 'D') continue;
      if (m.status !== EXPENSE_STATUS.DRAFT && m.status !== EXPENSE_STATUS.REJECTED) {
        showAlert(`${m.resolutionNo || uid}: 작성중·반려만 GW 연동결재할 수 있습니다.`);
        return;
      }
      const result = requestResolutionApproval(bundle.masters, bundle.details, uid);
      const masters = (result.masters || []).map((row) =>
        row.resolutionUid === uid ? { ...row, gwLinked: 'Y' } : row
      );
      bundle = { masters, details: result.details };
    }
    await saveResolutionBundle(d, bundle.masters, bundle.details);
    handleSearch();
    showAlert(`GW 연동결재(PoC stub) ${uids.length}건 — 승인요청 상태로 전환했습니다.`, 'success');
  }, [getSelectedIssuedResolutionUids, dims, handleSearch]);

  const { handleActiveAdd, handleActiveDelete, activeLabel } = useActiveGridCrudActions({
    guardAdd: (id) =>
      id === PAYEE_GRID_ID ? null : '채주내역 그리드가 활성일 때 추가할 수 있습니다.',
    guardDelete: (id) => {
      if (id === PAYEE_GRID_ID) return null;
      if (id === RESULT_GRID_ID) return null;
      return '채주 또는 결의내역이 활성일 때 삭제하세요.';
    },
  });

  const onPayeeAdd = useCallback(() => {
    appendPayees([
      emptyResolutionDetailRow({
        detailUid: createResolutionDetailUid(),
        resolutionUid: '',
        evidenceType: '수기',
        mod: 'A',
      }),
    ]);
  }, [appendPayees]);

  const onActiveDelete = useCallback(async () => {
    await payeeGrid.handleDelete?.();
    refreshPayeeSum();
  }, [payeeGrid, refreshPayeeSum]);

  const payeeColumns = useMemo(
    () => [
      hiCol.edTextColumn({ header: '채주코드', field: 'vendorCode', size: 90 }),
      hiCol.edTextColumn({ header: '채주명', field: 'vendorName', size: 130 }),
      hiCol.edTextColumn({ header: '증빙유형', field: 'evidenceType', size: 90 }),
      hiCol.edTextColumn({ header: '증빙번호', field: 'evidenceNo', size: 110 }),
      hiCol.edNumberColumn({ header: '금액', field: 'amount', size: 110, align: 'right' }),
      hiCol.edComboColumn({
        header: '부가세',
        field: 'vatYn',
        size: 70,
        options: [
          { value: 'N', label: 'N' },
          { value: 'Y', label: 'Y' },
        ],
      }),
      hiCol.edNumberColumn({ header: '부가세액', field: 'vatAmt', size: 90, align: 'right' }),
      hiCol.edTextColumn({ header: '은행', field: 'bankName', size: 90 }),
      hiCol.edTextColumn({ header: '계좌', field: 'accountNo', size: 130 }),
      hiCol.edTextColumn({ header: '비고', field: 'remark', size: 140 }),
    ],
    []
  );

  const projectColumns = useMemo(
    () => [
      hiCol.roTextColumn({ header: '부서코드', field: 'deptCode', size: 90 }),
      hiCol.roTextColumn({ header: '부서명', field: 'deptName', size: 110 }),
      hiCol.roTextColumn({ header: '사업코드', field: 'projectCode', size: 100 }),
      hiCol.roTextColumn({ header: '사업명', field: 'projectName', size: 150 }),
      hiCol.roTextColumn({ header: '재원', field: 'fundName', size: 80 }),
      hiCol.roNumberColumn({ header: '현액', field: 'balanceAmt', size: 110, align: 'right' }),
      hiCol.roNumberColumn({ header: '가용', field: 'availableAmt', size: 110, align: 'right' }),
      hiCol.roNumberColumn({ header: '과목수', field: 'subjectCount', size: 70, align: 'right' }),
    ],
    []
  );

  const subjectColumns = useMemo(
    () => [
      hiCol.roTextColumn({ header: '과목코드', field: 'subjectCode', size: 100 }),
      hiCol.roTextColumn({ header: '과목명', field: 'subjectName', size: 150 }),
      hiCol.roTextColumn({ header: '재원', field: 'fundName', size: 80 }),
      hiCol.roNumberColumn({ header: '현액', field: 'balanceAmt', size: 100, align: 'right' }),
      hiCol.roNumberColumn({ header: '가용', field: 'availableAmt', size: 100, align: 'right' }),
      hiCol.edNumberColumn({
        header: '배분금액',
        field: 'applyAmt',
        size: 110,
        align: 'right',
        title: '연결 시 채주 합계가 해당 과목 배분으로 기록됩니다',
      }),
    ],
    []
  );

  const resultColumns = useMemo(
    () => [
      hiCol.roTextColumn({ header: '결의번호', field: 'resolutionNo', size: 120, align: 'center' }),
      hiCol.roTextColumn({ header: '결의일', field: 'resolutionDate', size: 100, align: 'center' }),
      hiCol.roTextColumn({ header: '부서', field: 'deptName', size: 100 }),
      hiCol.roTextColumn({ header: '사업', field: 'projectName', size: 140 }),
      hiCol.roTextColumn({ header: '과목', field: 'subjectSummary', size: 120 }),
      hiCol.roNumberColumn({ header: '과목수', field: 'subjectCount', size: 70, align: 'right' }),
      hiCol.roNumberColumn({
        header: '결의금액',
        field: 'resolutionAmt',
        size: 110,
        align: 'right',
      }),
      hiCol.roTextColumn({ header: '상태', field: 'statusNm', size: 80, align: 'center' }),
      hiCol.roTextColumn({ header: 'GW', field: 'gwLinked', size: 50, align: 'center' }),
      hiCol.roTextColumn({ header: '적요', field: 'remark', size: 160 }),
    ],
    []
  );

  const searchFields = useMemo(
    () => [
      [
        {
          name: 'fiscalYear',
          label: '회계연도',
          type: 'fiscalYear',
          width: CommonCss.w80,
          labelWidth: SEARCH_LABEL_W,
        },
        {
          name: 'searchDeptDisp',
          label: '부서',
          type: 'text',
          width: CommonCss.w160,
          labelWidth: SEARCH_LABEL_W,
          readonly: true,
          showSearch: true,
          searchTitle: '부서 조회',
          placeholder: '전체',
          onSearch: () => setDeptLookupOpen(true),
          title: searchDeptNameWatch
            ? `선택: ${searchDeptNameWatch}`
            : '팝업에서 선택 · 비우면 전체 부서',
        },
        {
          name: 'includeFund',
          label: '재원구분',
          type: 'select',
          width: CommonCss.w120,
          labelWidth: SEARCH_LABEL_W,
          options: [
            { value: 'N', label: '부서+사업' },
            { value: 'Y', label: '부서+사업+재원' },
          ],
          title: '사업내역 집계 키 (재원은 옵션)',
        },
      ],
    ],
    [searchDeptNameWatch]
  );

  const deptLookupConfig = useMemo(() => {
    const rows = filterDepts(deptRows, { useYn: 'Y' }).map((r) => ({
      ...r,
      id: r.deptUid || r.deptCode,
    }));
    return {
      title: '부서 조회',
      rows,
      columns: [
        hiCol.roTextColumn({ header: '부서코드', field: 'deptCode', size: 100 }),
        hiCol.roTextColumn({ header: '부서명', field: 'deptName', size: 160 }),
      ],
      enableSearch: true,
      searchConfig: {
        mode: 'unified',
        keywordLabel: '검색',
        keywordFields: ['deptCode', 'deptName'],
      },
    };
  }, [deptRows]);

  const applyDeptLookup = useCallback(
    (payload) => {
      const rows = (Array.isArray(payload) ? payload : [payload]).filter(Boolean);
      if (!rows.length) {
        setValue('searchDeptCode', '');
        setValue('searchDeptName', '');
        setValue('searchDeptDisp', '');
        setDeptLookupOpen(false);
        return true;
      }
      const codes = rows.map((r) => String(r.deptCode || '').trim()).filter(Boolean);
      const names = rows.map((r) => String(r.deptName || '').trim()).filter(Boolean);
      setValue('searchDeptCode', codes.join(','));
      setValue('searchDeptName', names.join(','));
      setValue(
        'searchDeptDisp',
        names.length <= 1 ? names[0] || codes[0] || '' : `${names[0]} 외 ${names.length - 1}`
      );
      setDeptLookupOpen(false);
      return true;
    },
    [setValue]
  );

  useRegisterGridExport({
    id: PAYEE_GRID_ID,
    getData: () => payeeRef.current?.getData?.() || [],
    getDataColumns: () => payeeRef.current?.getDataColumns?.() || [],
    label: '채주내역',
  });
  useRegisterGridExport({
    id: RESULT_GRID_ID,
    getData: () => resultRef.current?.getData?.() || [],
    getDataColumns: () => resultRef.current?.getDataColumns?.() || [],
    label: '결의내역',
  });

  const { openDialog: openExcelDialog, dialog: excelDialog } = useExcelExportAction({
    fileName: '지출결의등록_type1',
  });

  const actionButtons = useMemo(
    () => [
      { id: 'search', label: '조회', icon: '🔍', onClick: handleSearch, hotkey: 'F7', title: '조회 (F7)' },
      {
        id: 'add',
        label: '추가',
        icon: '➕',
        onClick: handleActiveAdd,
        hotkey: 'F8',
        title: activeLabel ? `추가 → ${activeLabel}` : '채주 수기행 추가 (F8)',
      },
      {
        id: 'delete',
        label: '삭제',
        icon: '❌',
        onClick: async () => {
          const checked = resultRef.current?.getCheckedData?.() || [];
          if (checked.length) {
            await handleDeleteResolutions();
            return;
          }
          const sel = resultRef.current?.getSelectedRow?.();
          if (sel && (isType1DraftRow(sel) || sel.resolutionUid)) {
            await handleDeleteResolutions();
            return;
          }
          await handleActiveDelete();
        },
        hotkey: 'F9',
        variant: 'danger',
        title: '채주 행삭제 또는 결의/임시저장 삭제 (F9)',
      },
      {
        id: 'save',
        label: '저장',
        icon: '✅',
        onClick: handleSaveResolution,
        hotkey: 'F10',
        variant: 'success',
        title: '연결 묶음 → 결의번호 채번 (F10)',
      },
      {
        id: 'excel',
        label: '엑셀',
        icon: '📥',
        onClick: openExcelDialog,
        hotkey: 'F11',
      },
    ],
    [
      handleSearch,
      handleActiveAdd,
      handleActiveDelete,
      handleDeleteResolutions,
      handleSaveResolution,
      openExcelDialog,
      activeLabel,
    ]
  );

  const extraButtons = useMemo(
    () => [
      { id: 'request', label: '승인요청', onClick: handleRequestApproval },
      { id: 'gw', label: 'GW연동결재', onClick: handleGwApproval },
    ],
    [handleRequestApproval, handleGwApproval]
  );

  const draftBundleCount = summarizeDraftSession(draftSession).bundles.length;

  return (
    <PageContainer>
      <FormProvider {...methods}>
        <ActionButtonGroup buttons={actionButtons} extraButtons={extraButtons} />
        <WorkRow>
          <Stack {...scrollbarHoverProps}>
          <CollapsibleSearchPanel
            storageKey="expense-res-t1-search"
            prefsUserId={prefsUserId}
            title="조회조건"
          >
            <FormArea fields={searchFields} compact />
          </CollapsibleSearchPanel>

          <Hint>
            <strong>type1:</strong> 연결(과목1:증빙N) → 임시저장(
            {TYPE1_DRAFT_NO_LABEL}) 누적 → 저장(F10) 시 결의번호 1개 채번. 승인/GW는 채번
            건만. 전자증빙은 연결 시 임시점유, 삭제 시 미결 복귀.
            {activeLabel ? ` · 활성: ${activeLabel}` : ''}
            {selectedProject?.projectCode
              ? ` · 선택사업 ${selectedProject.projectCode}`
              : ''}
            {draftBundleCount ? ` · 임시묶음 ${draftBundleCount}개` : ''}
            {listCount ? ` · 목록 ${listCount}건` : ''}
          </Hint>

          {/* 1. 채주내역 */}
          <ActiveGridScope
            gridId={PAYEE_GRID_ID}
            onAdd={onPayeeAdd}
            onDelete={onActiveDelete}
            style={{ flex: '0 0 auto', minHeight: 'auto' }}
          >
            <SectionPanel $minHeight={`${PAYEE_BODY_H + 48}px`}>
              <ActiveGridHeader
                gridId={PAYEE_GRID_ID}
                extra={
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <ActionButton type="button" onClick={handleLinkBundle} title="과목1 : 채주N → 임시연결">
                      연결
                    </ActionButton>
                    <ActionButton type="button" onClick={() => setEvidenceOpen(true)}>
                      전자증빙 ···
                    </ActionButton>
                    <ActionButton type="button" onClick={openIncomeDialog}>
                      소득명세 ···
                    </ActionButton>
                    <span>수기(F8) · 채주합 {payeeSum.toLocaleString()}원</span>
                  </span>
                }
              >
                1. 채주내역
              </ActiveGridHeader>
              <GridBody style={{ height: PAYEE_BODY_H, flex: '0 0 auto' }}>
                <GridExportFocusScope gridId={PAYEE_GRID_ID}>
                  <HiGridComponent
                    ref={payeeRef}
                    columns={payeeColumns}
                    height="100%"
                    showRowNumber
                    rowNumberSize={56}
                    enableColumnFilters
                    enableContextMenu
                    enableRowEditMenu
                    enableCut
                    enableFindReplace
                    allowReplace
                    enableHeaderMenu
                    enableSelectionStats
                    enableFreeze
                    enableQuickExport
                    enableRowCheck
                    exportFileName="결의type1-채주"
                    gridId={PAYEE_GRID_ID}
                    prefsUserId={prefsUserId}
                    {...payeeGrid.contextMenuProps}
                  />
                </GridExportFocusScope>
              </GridBody>
            </SectionPanel>
          </ActiveGridScope>

          {/* 2.1 / 2.2 */}
          <MidRow>
            <ActiveGridScope
              gridId={PROJECT_GRID_ID}
              style={{ flex: '1 1 0', minHeight: 0, height: '100%' }}
            >
              <MidPanel>
                <ActiveGridHeader gridId={PROJECT_GRID_ID}>2.1 사업내역</ActiveGridHeader>
                <Meta>예산확정(현액) 부서+사업 집계 · 행 클릭 시 과목 조회 · 임시결의는 사업 1개만</Meta>
                <GridBody>
                  <HiGridComponent
                    ref={projectRef}
                    columns={projectColumns}
                    height="100%"
                    showRowNumber
                    rowNumberSize={56}
                    enableColumnFilters
                    enableHeaderMenu
                    enableSelectionStats
                    enableFreeze
                    enableQuickExport
                    enableRowCheck={false}
                    exportFileName="결의type1-사업"
                    gridId={PROJECT_GRID_ID}
                    prefsUserId={prefsUserId}
                    {...projectGrid.contextMenuProps}
                  />
                </GridBody>
              </MidPanel>
            </ActiveGridScope>

            <ActiveGridScope
              gridId={SUBJECT_GRID_ID}
              style={{ flex: '1 1 0', minHeight: 0, height: '100%' }}
            >
              <MidPanel>
                <ActiveGridHeader gridId={SUBJECT_GRID_ID}>
                  {selectedProject?.projectCode
                    ? `2.2 과목내역 (${selectedProject.projectCode})`
                    : '2.2 과목내역'}
                </ActiveGridHeader>
                <Meta>연결 시 과목 1개만 체크 · 다과목은 연결을 여러 번</Meta>
                <GridBody>
                  <HiGridComponent
                    ref={subjectRef}
                    columns={subjectColumns}
                    height="100%"
                    showRowNumber
                    rowNumberSize={56}
                    enableColumnFilters
                    enableHeaderMenu
                    enableSelectionStats
                    enableFreeze
                    enableQuickExport
                    enableRowCheck
                    exportFileName="결의type1-과목"
                    gridId={SUBJECT_GRID_ID}
                    prefsUserId={prefsUserId}
                    {...subjectGrid.contextMenuProps}
                  />
                </GridBody>
              </MidPanel>
            </ActiveGridScope>
          </MidRow>

          {/* 3. 결의내역 */}
          <ActiveGridScope
            gridId={RESULT_GRID_ID}
            onDelete={handleDeleteResolutions}
            style={{ flex: '0 0 auto', minHeight: 'auto' }}
          >
            <SectionPanel $minHeight={`${RESULT_BODY_H + 64}px`}>
              <ActiveGridHeader
                gridId={RESULT_GRID_ID}
                extra={
                  <ActionButton
                    type="button"
                    onClick={handleOpenDetailClick}
                    title="선택 결의의 과목·증빙 상세 (더블클릭)"
                  >
                    상세 ···
                  </ActionButton>
                }
              >
                3. 결의내역
              </ActiveGridHeader>
              <Meta>
                임시저장({TYPE1_DRAFT_NO_LABEL}) + 채번 결의 · 행 더블클릭 또는 [상세]로 증빙 확인 ·
                승인/GW는 채번 건만
              </Meta>
              <GridBody style={{ height: RESULT_BODY_H, flex: '0 0 auto' }}>
                <GridExportFocusScope gridId={RESULT_GRID_ID}>
                  <HiGridComponent
                    ref={resultRef}
                    columns={resultColumns}
                    height="100%"
                    showRowNumber
                    rowNumberSize={56}
                    enableColumnFilters
                    enableHeaderMenu
                    enableSelectionStats
                    enableFreeze
                    enableQuickExport
                    enableRowCheck
                    exportFileName="결의type1-목록"
                    gridId={RESULT_GRID_ID}
                    prefsUserId={prefsUserId}
                    {...resultGrid.contextMenuProps}
                  />
                </GridExportFocusScope>
              </GridBody>
            </SectionPanel>
          </ActiveGridScope>
          </Stack>
          <ExpenseType1ChatPanel
            prefsUserId={prefsUserId}
            getContext={getChatContext}
            onDone={handleChatDone}
          />
        </WorkRow>
      </FormProvider>

      <CodeLookupDialog
        open={evidenceOpen}
        title={evidenceLookupConfig.title}
        tabs={evidenceLookupConfig.tabs}
        defaultTabId={evidenceLookupConfig.defaultTabId}
        enableSearch={evidenceLookupConfig.enableSearch}
        searchConfig={evidenceLookupConfig.searchConfig}
        multiSelect
        prefsUserId={prefsUserId}
        sizeKey="codeLookup-expense-res-t1-evidence"
        onClose={() => setEvidenceOpen(false)}
        onSelect={applyEvidences}
      />

      <CodeLookupDialog
        open={deptLookupOpen}
        title={deptLookupConfig.title}
        columns={deptLookupConfig.columns}
        rows={deptLookupConfig.rows}
        enableSearch={deptLookupConfig.enableSearch}
        searchConfig={deptLookupConfig.searchConfig}
        multiSelect
        prefsUserId={prefsUserId}
        sizeKey="codeLookup-expense-res-t1-dept"
        onClose={() => setDeptLookupOpen(false)}
        onSelect={applyDeptLookup}
      />

      <IncomeStatementDialog
        open={incomeOpen}
        onClose={() => setIncomeOpen(false)}
        dims={dims()}
        draftSessionUid={draftSession?.draftSessionUid || ''}
        resolutionAmt={payeeSum}
        prefsUserId={prefsUserId}
        onSaved={handleIncomeSaved}
      />

      <ExpenseResolutionType1DetailDialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        master={detailMaster}
        bundles={detailBundles}
        prefsUserId={prefsUserId}
      />

      {excelDialog}
    </PageContainer>
  );
}

export default function ExpenseResolutionType1Workspace() {
  return (
    <GridExportProvider>
      <ActiveGridProvider>
        <ExpenseResolutionType1Inner />
      </ActiveGridProvider>
    </GridExportProvider>
  );
}
